import BlogCard from 'components/cards/BlogCard'
import Pill from 'components/Pill'
import { Post } from 'contentlayer/generated'
import useSearch from 'hooks/useSearch'
import { CoreContent } from 'lib/contentlayer'
import { getAllTags } from 'lib/tags'
import Link from 'next/link'
import { useRouter } from 'next/router'

interface Props {
  posts: CoreContent<Post>[]
  title: string
  description?: string
}

const getRandomPostTitle = (posts: CoreContent<Post>[]) => {
  const randomIndex = Math.floor(Math.random() * posts.length)
  const randomPost = posts[randomIndex]
  return randomPost.title
}

export default function ListLayout({ posts, title, description = '' }: Props) {
  const { results, searchValue, setSearchValue } = useSearch<CoreContent<Post>>({
    dataSet: posts,
    keys: ['title', 'summary'],
  })
  const router = useRouter()
  const allTagsMap = getAllTags(posts)
  const allTagsList = Object.keys(allTagsMap)
  const randomPostTitle = getRandomPostTitle(posts)

  const currentActiveTag = router?.query?.tag ? router.query.tag : router.asPath

  return (
    <>
      <div>
        <div className="pt-6 pb-8 space-y-2 md:space-y-5" data-aos="zoom-in-down">
          <h1 className="text-3xl font-extrabold leading-9 tracking-tight text-gray-900 dark:text-gray-100 sm:text-4xl sm:leading-10 md:text-6xl md:leading-14">
            {title}
          </h1>
          <p className="text-lg leading-7 text-gray-500 dark:text-gray-300">{description}</p>
          {/* Search Field */}
          <div className="relative">
            <input
              aria-label={`Try "${randomPostTitle}"`}
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder={`Try "${randomPostTitle}"`}
              className="block w-full px-4 py-2 pr-10 text-gray-900 bg-white border border-gray-300 rounded-md dark:border-gray-900 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-gray-100"
            />
            <svg
              aria-hidden="true"
              className="absolute w-5 h-5 text-gray-400 right-3 top-3 dark:text-gray-300"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          {/* Tags selection list */}
          {allTagsList.length > 0 && (
            <div className="flex overflow-x-auto snap-x pb-6 w-full">
              <Link href="/blogs/">
                <a>
                  <Pill active={currentActiveTag === '/blogs/'}>All</Pill>
                </a>
              </Link>
              {allTagsList.map((tag) => (
                <Link href={`/tag/${tag}`} key={tag}>
                  <a className="capitalize snap-center whitespace-nowrap">
                    <Pill active={currentActiveTag === tag}>{`${tag} (${allTagsMap[tag]})`}</Pill>
                  </a>
                </Link>
              ))}
            </div>
          )}
        </div>
        {!results.length && <p className="text-center my-10 text-2xl">No posts found.</p>}
        <ul className="mt-6 mx-auto grid gap-10 md:grid-cols-2 lg:grid-cols-3 lg:max-w-none">
          {results.map((frontMatter, idx) => {
            const { slug } = frontMatter
            return (
              <li key={slug} data-aos="fade-up" data-aos-delay={idx > 4 ? 250 : (idx + 1) * 100}>
                <BlogCard frontMatter={frontMatter} />
              </li>
            )
          })}
        </ul>
      </div>
    </>
  )
}
